/**
 * ERGO 360 Data Section
 * 展示人体工学产品统计数据的动画控制
 *
 * 动画效果（使用 GSAP）：
 * 1. 视频背景：scale 1.1 → 1 缩小进场，入场播放一次（不循环）
 * 2. 副标题：从下淡入飘入
 * 3. 主标题：从下淡入飘入
 * 4. 数据卡片：依次从下淡入飘入
 * 5. 大数字：计数器效果（countdown）
 */

class Ergo360Data {
  constructor(section) {
    this.section = section;
    if (!this.section) return;

    this.animated = false;
    this.videoPlayed = false; // 视频是否已播放过
    this.cacheElements();
    this.init();
  }

  /**
   * 缓存 DOM 元素
   */
  cacheElements() {
    this.video = this.section.querySelector('.ergo-360-data__video');
    this.subtitle = this.section.querySelector('.ergo-360-data__subtitle');
    this.titles = this.section.querySelectorAll('.ergo-360-data__title'); // 选择所有标题（PC和移动端）
    this.statItems = this.section.querySelectorAll('.ergo-360-data__stat-item');
    this.statNumbers = this.section.querySelectorAll('.ergo-360-data__stat-number');
  }

  /**
   * 预加载视频（仅 PC 端）
   */
  preloadVideo() {
    // 移动端不加载视频，避免不必要的带宽消耗
    if (window.innerWidth < 1024) return;

    if (this.video && this.video.dataset.src && !this.video.src) {
      this.video.src = this.video.dataset.src;
      this.video.preload = 'auto';
      this.video.load();
    }
  }

  /**
   * 初始化
   */
  init() {
    // 使用 IntersectionObserver 检测进入视口
    // rootMargin: '0px 0px -50% 0px' = section 顶边进入视窗 50% 位置时触发
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 进入视口时预加载视频
            this.preloadVideo();

            // 播放入场动画（只播放一次）
            if (!this.animated) {
              this.animated = true;
              this.playEntryAnimation();
            }

            // 视频只在首次入场时播放一次
            if (this.video && !this.videoPlayed) {
              this.videoPlayed = true;
              this.video.play().catch(() => {});
            }
          }
          // 移除离开视口暂停的逻辑，让视频自然播完
        });
      },
      {
        threshold: 0,
        rootMargin: '0px 0px -50% 0px',
      }
    );

    this.observer.observe(this.section);
  }

  /**
   * 播放进场动画
   */
  playEntryAnimation() {
    // 等待 GSAP 加载
    if (typeof gsap === 'undefined') {
      setTimeout(() => this.playEntryAnimation(), 100);
      return;
    }

    // 创建动画时间线
    const tl = gsap.timeline();

    // 1. 视频缩小动画（同时进行）
    if (this.video) {
      gsap.to(this.video, {
        scale: 1,
        duration: 1.2,
        ease: 'power2.out',
      });
    }

    // 2. 副标题从下淡入
    if (this.subtitle) {
      tl.fromTo(
        this.subtitle,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' }
      );
    }

    // 3. 主标题从下淡入（副标题快结束时开始）- PC和移动端标题同时动画
    if (this.titles.length > 0) {
      tl.fromTo(
        this.titles,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
        '-=0.4' // 提前 0.4s 开始
      );
    }

    // 4. 数据卡片依次从下淡入
    if (this.statItems.length > 0) {
      tl.fromTo(
        this.statItems,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: 'power2.out',
          stagger: 0.15, // 每个卡片间隔 0.15s
        },
        '-=0.3' // 提前开始
      );

      // 5. 计数器效果（数据卡片开始动画后启动）
      tl.call(() => this.animateCounters(), null, '-=0.5');
    }
  }

  /**
   * 数字计数器动画（countdown 效果）
   */
  animateCounters() {
    this.statNumbers.forEach((el) => {
      const targetValue = parseInt(el.dataset.value, 10);
      const suffix = el.dataset.suffix || '';

      // 保存三角形图标元素
      const suffixIcon = el.querySelector('.ergo-360-data__stat-suffix-icon');
      const iconHTML = suffixIcon ? suffixIcon.outerHTML : '';

      // 使用 GSAP 的计数器动画
      const counter = { value: 0 };
      gsap.to(counter, {
        value: targetValue,
        duration: 2,
        ease: 'power2.out',
        onUpdate: () => {
          el.innerHTML = Math.round(counter.value) + suffix + ' ' + iconHTML;
        },
      });
    });
  }

  /**
   * 销毁
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

/**
 * 初始化所有 ERGO 360 Data sections
 */
function initErgo360DataSections() {
  const sections = document.querySelectorAll('.ergo-360-data');
  sections.forEach((section) => {
    // 避免重复初始化
    if (!section.dataset.initialized) {
      new Ergo360Data(section);
      section.dataset.initialized = 'true';
    }
  });
}

// DOM 加载完成后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initErgo360DataSections);
} else {
  initErgo360DataSections();
}

// 支持 Shopify Section Rendering API（主题编辑器实时预览）
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.ergo-360-data')) {
    initErgo360DataSections();
  }
});
